import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

type ToastVariant = 'success' | 'error' | 'info' | 'neutral';

type ToastItem = {
  id: number;
  message: string;
  variant: ToastVariant;
};

type ToastContextValue = {
  toast: (message: string, variant?: ToastVariant) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastVariant, React.CSSProperties> = {
  success: { borderLeftColor: 'var(--badge-approved-text)', background: 'var(--badge-approved-bg)' },
  error:   { borderLeftColor: 'var(--badge-rejected-text)', background: 'var(--badge-rejected-bg)' },
  info:    { borderLeftColor: 'var(--badge-info-text)',     background: 'var(--badge-info-bg)' },
  neutral: { borderLeftColor: 'var(--border-strong)',       background: 'var(--surface-base)' },
};

function ToastItem({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 14px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-default)',
        borderLeft: '3px solid',
        boxShadow: 'var(--shadow-raised)',
        animation: 'slideInRight 180ms ease',
        minWidth: 260,
        maxWidth: 360,
        ...VARIANT_STYLES[item.variant],
      }}
    >
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 500 }}>
        {item.message}
      </span>
      <button
        onClick={() => onDismiss(item.id)}
        aria-label="Close"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-tertiary)',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0,
          display: 'flex',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M1.5 1.5l9 9M10.5 1.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message: string, variant: ToastVariant = 'neutral') => {
    const id = ++counter.current;
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        aria-label="Notifications"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 200,
        }}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} item={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx.toast;
}
